(() => {
  'use strict';

  const TRANSIENT_PARAMS = ['page', 'section_id', 'sections', 'lic_resume', 'lic_return'];

  function priceCents(value) {
    if (value === null || String(value).trim() === '') return null;
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(String(value).trim())) return NaN;
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number * 100) : NaN;
  }

  function buildPriceUrl(href, minParam, maxParam, band) {
    const url = new URL(href);
    for (const name of [minParam, maxParam, ...TRANSIENT_PARAMS]) url.searchParams.delete(name);
    if (band.min !== null) url.searchParams.set(minParam, (band.min / 100).toFixed(2));
    if (band.max !== null) url.searchParams.set(maxParam, (band.max / 100).toFixed(2));
    return url.href;
  }

  function nativeRangeMaxCents(value, fallback) {
    const formatted = typeof value === 'string' ? value.trim() : '';
    if (!/^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/.test(formatted)) return fallback;
    const cents = priceCents(formatted.replace(/,/g, ''));
    return Number.isFinite(cents) ? cents : fallback;
  }

  function activePriceBand(params, minParam, maxParam, rangeMax, bands) {
    const min = priceCents(params.get(minParam));
    const max = priceCents(params.get(maxParam));
    if (Number.isNaN(min) || Number.isNaN(max)) return null;
    const lower = min === null ? 0 : min;
    const noUpper = max === null || Number.isFinite(rangeMax) && max >= rangeMax;

    // Match explicit bounded presets before interpreting native zero/full-max defaults.
    const preset = bands.find((band) => {
      if (band.key === 'all_prices') return false;
      const sameMin = band.min === null ? lower === 0 : min === band.min;
      const sameMax = band.max === null ? noUpper : max === band.max;
      return sameMin && sameMax;
    });
    if (preset) return preset.key;
    return lower === 0 && noUpper ? 'all_prices' : null;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { priceCents, buildPriceUrl, nativeRangeMaxCents, activePriceBand };
    return;
  }

  if (customElements.get('lic-collection-price-filter')) return;

  class CollectionPriceFilter extends HTMLElement {
    connectedCallback() {
      this.minParam = this.dataset.priceMinParam;
      this.maxParam = this.dataset.priceMaxParam;
      if (!this.minParam || !this.maxParam || this.controller) return;
      this.rangeMax = Number(this.dataset.priceRangeMax);
      this.controller = new AbortController();
      this.bands = [...this.querySelectorAll('a[data-price-band]')].map((link) => ({
        link,
        key: link.dataset.priceBand,
        min: link.dataset.priceMin === '' ? null : Number(link.dataset.priceMin),
        max: link.dataset.priceMax === '' ? null : Number(link.dataset.priceMax),
      }));
      this.refresh = this.refresh.bind(this);
      this.schedule = this.schedule.bind(this);
      this.observeGrid = this.observeGrid.bind(this);

      // Keep normal anchor navigation, including keyboard, modified and middle clicks.
      for (const type of ['pointerdown', 'mousedown', 'click', 'auxclick', 'contextmenu', 'focusin']) {
        this.addEventListener(type, this.refresh, { signal: this.controller.signal });
      }
      window.addEventListener('popstate', this.schedule, { signal: this.controller.signal });
      window.addEventListener('pageshow', this.schedule, { signal: this.controller.signal });
      document.addEventListener('shopify:section:load', this.observeGrid, { signal: this.controller.signal });
      this.observeGrid();
      this.refresh();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    observeGrid() {
      this.observer?.disconnect();
      const grid = document.getElementById('ProductGridContainer');
      if (grid) {
        this.observer = new MutationObserver(this.schedule);
        this.observer.observe(grid, { childList: true, subtree: true });
      }
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.isConnected) this.refresh();
      });
    }

    refresh() {
      const current = new URL(location.href);
      // Native facets may refresh the USD range maximum along with their filter HTML.
      const nativeMax = [...document.querySelectorAll('.filter__price_number[data-max]')]
        .find((input) => input.name === this.maxParam);
      const rangeMax = nativeRangeMaxCents(nativeMax?.dataset.max, this.rangeMax);
      const active = activePriceBand(current.searchParams, this.minParam, this.maxParam, rangeMax, this.bands);
      for (const band of this.bands) {
        band.link.href = buildPriceUrl(current.href, this.minParam, this.maxParam, band);
        if (band.key === active) band.link.setAttribute('aria-current', 'true');
        else band.link.removeAttribute('aria-current');
      }
    }
  }

  customElements.define('lic-collection-price-filter', CollectionPriceFilter);
})();
